import React, { useEffect } from 'react';
import { Portal } from 'react-portal';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import { theme, toRem } from 'design';
import { ModalOverlay } from '../Modal';
import AlertContent from './AlertContent';
import { IAlertContentProps } from './AlertProps';

const Container = styled(motion.div).attrs({ 'aria-hidden': 'true' })`
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${theme.zIndex.modal};
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: ${toRem(theme.layout.gutter.small)};
  background-color: transparent;
`;

interface IAlertProps extends IAlertContentProps {
  isActive?: boolean;
}

const Alert: React.FC<IAlertProps> = ({
  title,
  message,
  hideLeftAction,
  onLeftAction,
  onRightAction,
  leftActionLabel,
  rightActionLabel,
  icon,
  iconFill,
  maxWidth,
  isLoading,
  isActive,
  rightActionAppearance,
  leftActionAppearance,
  dataCy = 'modal',
  ...rest
}) => {
  useEffect(() => {
    document.body.classList.toggle('prevent-scroll', isActive);
    return () => document.body.classList.toggle('prevent-scroll', false);
  }, [isActive]);

  return (
    <Portal>
      <AnimatePresence>
        {isActive && (
          <>
            <Container initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ ease: 'easeInOut', duration: 0.2 }} data-cy={dataCy} {...rest}>
              <AlertContent
                title={title}
                message={message}
                hideLeftAction={hideLeftAction}
                onLeftAction={onLeftAction}
                onRightAction={onRightAction}
                leftActionLabel={leftActionLabel}
                rightActionLabel={rightActionLabel}
                icon={icon}
                iconFill={iconFill}
                maxWidth={maxWidth}
                isLoading={isLoading}
                rightActionAppearance={rightActionAppearance}
                leftActionAppearance={leftActionAppearance}
              />
              <ModalOverlay data-cy="modal-overlay" />
            </Container>
          </>
        )}
      </AnimatePresence>
    </Portal>
  );
};

export default Alert;
